import { useState, useCallback, useMemo } from 'react';
import {
  QuizPlayerItem,
  QuizPlayerEvents,
  QuizPlayerValidation,
  QuizPlayerScreenType,
  QuizPlayerAttempt,
  QuizStats,
} from './types';
import { useQuizTimer } from './use-quiz-timer';
import { useControlledState } from '@/hooks/use-controlled-state';

export type UseQuizPlayerProps = QuizPlayerValidation & {
  questions?: QuizPlayerItem[];
  events?: QuizPlayerEvents;
  attempts?: QuizPlayerAttempt[];
  requireConfirm?: boolean;
  displayIndex?: number;
  defaultScreen?: 'welcome' | 'quiz';
};

export function useQuizPlayer({
  questions = [],
  events = {},
  attempts = [],
  displayIndex,
  validate,
  requireConfirm,
  defaultScreen = 'welcome',
}: UseQuizPlayerProps) {
  const [screen, setScreen] = useState<QuizPlayerScreenType>(defaultScreen);

  const stats = useMemo<QuizStats>(() => {
    const totalAttempts = attempts.length;

    // Score in percentage
    const avgScore =
      totalAttempts === 0
        ? 0
        : attempts.reduce((sum, i) => sum + i.score, 0) / totalAttempts;

    // Score scaled to the number of questions
    const avgScoreScaled = Math.round(questions.length * avgScore);

    return { totalAttempts, avgScore: avgScoreScaled };
  }, [attempts, questions]);

  const [currentIndex, setCurrentIndex] = useControlledState({
    value: displayIndex,
    defaultValue: 0,
    onChange: events.onNavigateTo,
  });

  const [selectedAnswer, setSelectedAnswer] = useState<unknown | null>(null);
  const [isAnswerValidated, setIsAnswerValidated] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [score, setScore] = useState(0);

  const { isTimerActive, pauseTimer, resetTimer } = useQuizTimer();
  const currentQuestion = questions[currentIndex];

  const validateAnswer = async (choice: unknown) => {
    if (!currentQuestion) return;

    let isAnswerCorrect = choice === currentQuestion.correctAnswer;
    console.log({ choice, currentQuestion, isAnswerCorrect });
    if (validate) {
      try {
        isAnswerCorrect = await validate(choice, currentQuestion.correctAnswer);
      } catch (error) {
        console.error('Validation error:', error);
      }
    }

    setIsCorrect(isAnswerCorrect);
    setIsAnswerValidated(true);

    if (isAnswerCorrect) {
      setScore((prev) => prev + 1);
    }

    events.onAnswer?.(currentQuestion.id, choice, isAnswerCorrect);
    pauseTimer();
  };

  const start = () => {
    setScreen('quiz');
    events.onStart?.();
  };

  const reset = () => {
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setIsAnswerValidated(false);
    resetTimer();
    setScore(0);
  };

  const finish = useCallback(() => {
    setScreen('end');
    events.onFinish?.(score, questions.length);
  }, [events, score, questions]);

  const handleAnswerSelection = (choice: unknown) => {
    setSelectedAnswer(choice);
    if (!requireConfirm) {
      validateAnswer(choice);
    }
  };

  const handleConfirmAnswer = () => {
    if (selectedAnswer && !isAnswerValidated) {
      validateAnswer(selectedAnswer);
    }
  };

  const handleTimeUp = useCallback(() => {
    if (currentQuestion && !isAnswerValidated) {
      events.onTimeUp?.(currentQuestion.id);
      setIsAnswerValidated(true);
      setIsCorrect(false);
    }
  }, [currentQuestion, events, isAnswerValidated]);

  const handleNext = useCallback(() => {
    if (!selectedAnswer && !isAnswerValidated) return;

    if (currentIndex === questions.length - 1) {
      finish();
      return;
    }

    setCurrentIndex((prev) => prev + 1);
    setSelectedAnswer(null);
    setIsAnswerValidated(false);
    resetTimer();
  }, [
    currentIndex,
    questions.length,
    selectedAnswer,
    isAnswerValidated,
    resetTimer,
    setCurrentIndex,
    finish,
  ]);

  const handleCancel = () => {
    reset();
    setScreen(defaultScreen);
    events.onCancel?.();
  };

  const handleRestart = () => {
    if (screen !== 'end') return;

    reset();
    setScreen('quiz');
    events.onRestart?.();
  };

  return {
    currentQuestion,
    currentIndex,
    selectedAnswer,
    isAnswerValidated,
    isCorrect,
    score,
    screen,
    stats,
    handleAnswerSelection,
    handleConfirmAnswer,
    handleNext,
    handleCancel,
    handleTimeUp,
    handleStart: start,
    handleRestart,
    isTimerActive,
  };
}

export type QuizPlayerControl = ReturnType<typeof useQuizPlayer>;
